// The store dashboard (server: getFashionStoreDashboard): a day's store sales
// for the manager on the till, and the open work as it stands now. Sales come
// from the server's completed bills, so a bill still waiting to sync from an
// offline till shows once it has synced. The server checks the same day rule
// as validateDashboardDay below.
import { FASHION_FEATURES } from "./features";

export const MAX_DASHBOARD_DAYS_BACK = 366;
export const TOP_STYLES = 8;

const clean = (s) => String(s ?? "").replace(/["\\]/g, "").replace(/[\r\n]+/g, " ").trim();
const str = (s) => `"${clean(s)}"`;
const FIELDS =
  "date bills sales units atv upt returns returnValue netSales onlineOrders onlineSales onlineNew onlineAccepted onlineReady " +
  "holdsActive holdsExpired alterationsDue alterationsReady followUpsDue wishesInStock countDrafts " +
  "topProducts { productId sku name qty sales } hours { hour bills sales }";

export const buildDashboardQuery = (bunitId, date) => ({
  query: `query { getFashionStoreDashboard(bunitId: ${str(bunitId)}${date ? `, date: ${str(date)}` : ""}) { ${FIELDS} }}`,
});

const n = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));
const z = (v) => n(v) ?? 0;

// Server response → { dashboard } or { error }.
export function readDashboard(response) {
  const fallback = "Couldn't load the dashboard. Check the connection and try again.";
  if (!response) return { error: fallback };
  const errors = response?.data?.errors;
  if (errors?.length) return { error: errors[0]?.message || fallback };
  const d = response?.data?.data?.getFashionStoreDashboard;
  if (d === null) return { error: "This store's dashboard isn't open to you, or the day is out of range." };
  if (!d || typeof d !== "object") return { error: fallback };
  return {
    dashboard: {
      date: d.date || "",
      bills: z(d.bills),
      sales: z(d.sales),
      units: z(d.units),
      atv: n(d.atv),
      upt: n(d.upt),
      returns: z(d.returns),
      returnValue: z(d.returnValue),
      netSales: z(d.netSales),
      onlineOrders: n(d.onlineOrders),
      onlineSales: n(d.onlineSales),
      // Open work: null when the server couldn't read it, which isn't the same as none.
      onlineNew: n(d.onlineNew),
      onlineAccepted: n(d.onlineAccepted),
      onlineReady: n(d.onlineReady),
      holdsActive: n(d.holdsActive),
      holdsExpired: n(d.holdsExpired),
      alterationsDue: n(d.alterationsDue),
      alterationsReady: n(d.alterationsReady),
      followUpsDue: n(d.followUpsDue),
      wishesInStock: n(d.wishesInStock),
      countDrafts: n(d.countDrafts),
      topProducts: (Array.isArray(d.topProducts) ? d.topProducts : []).filter(Boolean).map((p) => ({
        productId: p.productId || "",
        sku: p.sku || "",
        name: p.name || p.sku || "",
        qty: z(p.qty),
        sales: z(p.sales),
      })),
      hours: (Array.isArray(d.hours) ? d.hours : [])
        .filter((h) => h && Number.isInteger(Number(h.hour)))
        .map((h) => ({ hour: Number(h.hour), bills: z(h.bills), sales: z(h.sales) }))
        .sort((a, b) => a.hour - b.hour),
    },
  };
}

const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const todayIso = (now = new Date()) => isoDay(now);
export const firstDashboardDay = (now = new Date()) => isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - MAX_DASHBOARD_DAYS_BACK));

// The picked day → null when fine, or what's wrong, in the server's words.
export function validateDashboardDay(day, now = new Date()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day || "")) || Number.isNaN(Date.parse(day))) return "Pick a day.";
  if (day > todayIso(now)) return "Pick today or an earlier day.";
  if (day < firstDashboardDay(now)) return "The dashboard goes back a year.";
  return null;
}

// Top-selling sizes → styles, each with its sizes, most pieces first. A size
// the till doesn't know (not synced here) stands as a style of its own.
export function topStyles(topProducts, productById, styleById, limit = TOP_STYLES) {
  const groups = new Map();
  for (const p of topProducts || []) {
    const product = productById?.(p.productId);
    const styleId = product?.parentProductId || p.productId;
    const style = styleById?.get?.(styleId);
    if (!groups.has(styleId)) groups.set(styleId, { styleId, name: style?.name || (product?.parentProductId ? null : product?.name) || p.name, qty: 0, sales: 0, sizes: [] });
    const g = groups.get(styleId);
    if (!g.name) g.name = p.name;
    g.qty += p.qty;
    g.sales += p.sales;
    g.sizes.push({ productId: p.productId, label: product?.name || p.name, qty: p.qty });
  }
  return [...groups.values()].sort((a, b) => b.qty - a.qty || b.sales - a.sales).slice(0, limit);
}

export const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

// The hour with the most sales, or null on a day without bills.
export function busiestHour(hours) {
  let best = null;
  for (const h of hours || []) if (h.bills > 0 && (!best || h.sales > best.sales)) best = h;
  return best;
}

// Open work → rows to act on, most pressing first. Each names the till screen
// that deals with it; a count the server couldn't read is left out, and so are
// the hold rows while holds are switched off (FASHION_FEATURES).
export function workRows(d, features = FASHION_FEATURES) {
  if (!d) return [];
  const rows = [
    { key: "onlineNew", label: "Online orders to accept", count: d.onlineNew, panel: "online", urgent: true },
    { key: "holdsExpired", label: "Expired holds to put back", count: d.holdsExpired, panel: "holds", urgent: true },
    { key: "alterationsDue", label: "Alterations due and not ready", count: d.alterationsDue, panel: "alterations", urgent: true },
    { key: "onlineReady", label: "Online orders ready to hand over", count: d.onlineReady, panel: "online" },
    { key: "onlineAccepted", label: "Online orders being picked", count: d.onlineAccepted, panel: "online" },
    { key: "alterationsReady", label: "Alterations ready for pickup", count: d.alterationsReady, panel: "alterations" },
    { key: "followUpsDue", label: "Customer calls due", count: d.followUpsDue, panel: "followups" },
    { key: "wishesInStock", label: "Wishes now in stock", count: d.wishesInStock, panel: "followups" },
    { key: "holdsActive", label: "Sizes on hold", count: d.holdsActive, panel: "holds" },
    { key: "countDrafts", label: "Stock counts waiting for the back office", count: d.countDrafts, panel: null },
  ];
  return rows
    .filter((r) => r.count != null && (features.holds || r.panel !== "holds"))
    .map((r) => ({ ...r, urgent: Boolean(r.urgent && r.count > 0) }));
}
